import filterByTerm from "utils/filterByTerm";

const filterFieldByVariety = (field, filterVarieties = []) => {
  if (filterVarieties.length) {
    const isIncludedInFilters = filterVarieties.some(
      (variety) => variety.value === field.varietyRead?.varietyId
    );
    return isIncludedInFilters;
  }
  return true;
};

const filterFieldByMunicipality = (field, filterMunicipalities = []) => {
  if (filterMunicipalities.length) {
    const isIncludedInFilters = filterMunicipalities.some(
      (mun) => mun.value === field.municipalityId
    );
    return isIncludedInFilters;
  }
  return true;
};
const filterFarmerByVariety = (farmer, filterVarieties = []) => {
  if (filterVarieties.length) {
    const isIncludedInFilters =
      filterVarieties.findIndex((variety) =>
        farmer.varieties.includes(variety.value)
      ) !== -1;
    return isIncludedInFilters;
  }
  return true;
};

const filterFarmerByMunicipality = (farmer, filterMunicipalities = []) => {
  if (filterMunicipalities.length) {
    const isIncludedInFilters =
      filterMunicipalities.findIndex((mun) =>
        farmer.municipalities.includes(mun.value)
      ) !== -1;
    return isIncludedInFilters;
  }
  return true;
};

const filterFarmerByStatus = (farmer, filterStatus = []) => {
  if (filterStatus.length) {
    return filterStatus.some((status) => status.value === farmer.status);
  }
  return true;
};

const filterFieldByStatus = (field, filterStatus = []) => {
  if (filterStatus.length) {
    return filterStatus.some((status) => status.value === field.status);
  }
  return true;
};

const filterFieldByCropType = (field, cropType) => {
  const cropTypeValue = cropType.value;
  if (!cropTypeValue) return true;
  if (cropTypeValue === "all") return true;
  if (cropTypeValue === "bio") return Boolean(field.isBioCrop);
  if (cropTypeValue === "conventional") return Boolean(!field.isBioCrop);
};

const filterFarmerByCropType = (farmer, cropType) => {
  const cropTypeValue = cropType.value;
  if (!cropTypeValue) return true;
  if (cropTypeValue === "all") return true;
  if (cropTypeValue === "bio") return farmer.bio;
  if (cropTypeValue === "conventional") return farmer.nonBio;
};

const filterFieldByEcoScheme = (field, ecoSchemes) => {
  if (ecoSchemes.length) {
    const isIncludedInFilters = ecoSchemes.some((scheme) =>
      field.ecoschemes?.some((fieldScheme) => fieldScheme === scheme.value)
    );
    return isIncludedInFilters;
  }
  return true;
};

const filterFarmerByEcoScheme = (farmer, ecoSchemes) => {
  if (ecoSchemes.length) {
    const isIncludedInFilters = ecoSchemes.some((scheme) =>
      farmer.ecoschemes?.some((farmerScheme) => farmerScheme === scheme.value)
    );
    return isIncludedInFilters;
  }
  return true;
};

const getUsersAndFieldsWithLabel = (labels = []) => {
  const selectedLabelsIds = labels.map((label) => label.id);

  const userIdsWithLabelSet = new Set();
  const fieldIdsWithLabelSet = new Set();

  labels?.forEach((label) => {
    const labelId = label.id;
    if (!selectedLabelsIds.includes(labelId)) return;

    label.users?.forEach((id) => userIdsWithLabelSet.add(id));
    label.fields?.forEach((id) => fieldIdsWithLabelSet.add(id));
  });
  return { userIdsWithLabelSet, fieldIdsWithLabelSet };
};

const filterFarmerByLabel = (farmer, labels = []) => {
  if (!labels.length) return true;

  const { userIdsWithLabelSet, fieldIdsWithLabelSet } =
    getUsersAndFieldsWithLabel(labels);

  if (userIdsWithLabelSet.has(farmer.id)) return true;
  if (Array.isArray(farmer.fields)) {
    return farmer.fields.some((field) => fieldIdsWithLabelSet.has(field.id));
  }
  return false;
};

const filterFieldByLabel = (field, labels = []) => {
  if (!labels.length) return true;

  const { userIdsWithLabelSet, fieldIdsWithLabelSet } =
    getUsersAndFieldsWithLabel(labels);

  if (fieldIdsWithLabelSet.has(field.id)) return true;
  if (field.farmer && userIdsWithLabelSet.has(field.farmer.id)) return true;
  return false;
};

export const filterFarmers = (farmers, filters) => {
  const farmersResult = farmers.filter((farmer) => {
    const isStatusIncluded = filterFarmerByStatus(farmer, filters?.status);
    if (!isStatusIncluded) return false;

    const isSearchTermIncluded = filterByTerm.user(farmer, filters.searchTerm);
    if (!isSearchTermIncluded) return false;

    const isEcoSchemeIncluded = filterFarmerByEcoScheme(
      farmer,
      filters?.ecoSchemes
    );
    if (!isEcoSchemeIncluded) return false;

    const isCropTypeIncluded = filterFarmerByCropType(
      farmer,
      filters?.cropType
    );
    if (!isCropTypeIncluded) return false;

    const isVarietyIncluded = filterFarmerByVariety(farmer, filters?.varieties);
    if (!isVarietyIncluded) return false;

    const isMunicipalityIncluded = filterFarmerByMunicipality(
      farmer,
      filters?.municipalities
    );
    if (!isMunicipalityIncluded) return false;

    const isLabelIncluded = filterFarmerByLabel(farmer, filters?.labels);
    if (!isLabelIncluded) return false;

    return true;
  });

  return farmersResult;
};

export const filterFields = (fields, filters) => {
  const fieldsResult = fields.filter((field) => {
    const isStatusIncluded = filterFieldByStatus(field, filters?.status);
    if (!isStatusIncluded) return false;

    const isEcoSchemeIncluded = filterFieldByEcoScheme(
      field,
      filters?.ecoSchemes
    );
    if (!isEcoSchemeIncluded) return false;

    const isSearchTermIncluded = filterByTerm.user(
      field.farmer,
      filters.searchTerm
    );
    if (!isSearchTermIncluded) return false;

    const isCropTypeIncluded = filterFieldByCropType(field, filters?.cropType);
    if (!isCropTypeIncluded) return false;

    const isVarietyIncluded = filterFieldByVariety(field, filters?.varieties);
    if (!isVarietyIncluded) return false;

    const isMunicipalityIncluded = filterFieldByMunicipality(
      field,
      filters?.municipalities
    );
    if (!isMunicipalityIncluded) return false;

    const isLabelIncluded = filterFieldByLabel(field, filters?.labels);
    if (!isLabelIncluded) return false;

    return true;
  });

  return fieldsResult;
};
